import React, { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { Flex } from "@fluentui/react-northstar";
import pl from "shared/messages/pl";
import { LinkButton, RegularText } from "shared/components";
import {
    CommissionTablesSection,
    ContractsTable,
    DynamiAttributesSection,
    EntityDetailsItem,
    ProfilesTable,
    ShowMoreButton,
} from "view360Card/components";
import {
    DictionaryItem,
    Intermediary,
    Representative,
    SearchEntityTypeEnum,
    UserBasicEntity,
} from "view360Card/models";
import { constants } from "shared/constants";
import { formatAddress, formatDate } from "utils/formattingUtils";
import { getValuesOrDefaultRowView } from "utils/entityUtils";
import { Tag } from "shared/models/Tag";
import TagLabels from "msTeamsUI/components/Board/TagLabels";
import { useNavigate } from "react-router-dom";
import { getLinkToEntity } from "shared/routing/pages";
import { LocationCard } from "../locationCard/LocationCard";
import { AadUserDetails } from "modules/aad-user-details";
import { MsTeamsContext } from "msTeams/store";

export interface GeneralInformationItermediaryProps {
    intermediary: Intermediary;
}

export const GeneralInformationIntermediary: React.FC<GeneralInformationItermediaryProps> = observer((props) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const msTeamsContext = useContext(MsTeamsContext);
    const navigation = useNavigate();

    const getSalesPersonLink = (representative: Representative) => {
        const text = `${representative.firstName} ${representative.lastName} ${constants.inlineSeparator} ${
            representative.punu
        } - ${
            representative.isOwner
                ? pl.view360Card.generalInformations.owner
                : pl.view360Card.generalInformations.representative
        }`;

        if (representative.salesPersonId) {
            return (
                <LinkButton
                    key={representative.id}
                    text={text}
                    onClick={() => {
                        navigation(
                            getLinkToEntity(SearchEntityTypeEnum.SalesPerson, representative.salesPersonId ?? 0)
                        );
                    }}
                />
            );
        }

        return <RegularText key={representative.id} content={text} />;
    };

    const getRepresentatives = () => {
        return (
            <Flex column>
                {props.intermediary.representativeList.map((r) => {
                    return getSalesPersonLink(r);
                })}
            </Flex>
        );
    };

    const getAgencyRegistrationData = () => {
        if (!props.intermediary.address && !props.intermediary.email && !props.intermediary.phone) {
            return null;
        }

        const address = formatAddress(props.intermediary.address);

        return <LocationCard address={address ?? ""} />;
    };

    const getUserData = (user: UserBasicEntity | undefined) => {
        if (!user) {
            return null;
        }

        return (
            <AadUserDetails
                fullName={user.fullName!}
                email={user.userPrincipalName}
                phoneNumber={user.mobilePhone}
                mobileClient={msTeamsContext.isMobileClient}
            />
        );
    };

    const getLabels = (locationtags: DictionaryItem[]) => {
        if (locationtags === undefined) return null;

        const tags: Tag[] = locationtags.map((t) => ({
            color: t.flexField1 ?? constants.defaultTagColor,
            description: t.code ?? "",
        }));

        return <TagLabels tags={tags} />;
    };

    const getProfilesTable = () => {
        if (props.intermediary.profileList.length === 0) {
            return null;
        }
        return <ProfilesTable profiles={props.intermediary.profileList} />;
    };

    const getContractsTable = () => {
        if (props.intermediary.contractList.length === 0) {
            return null;
        }
        return <ContractsTable contracts={props.intermediary.contractList} />;
    };

    const getCommissionTablesSection = () => {
        if (props.intermediary.commissionSystemList.length === 0) {
            return null;
        }
        return <CommissionTablesSection commissionSystemList={props.intermediary.commissionSystemList} />;
    };

    const expandButton = React.useMemo(() => {
        if (!(props.intermediary.locationList.length > constants.tableMaxDefaultSize)) {
            return null;
        }

        return (
            <Flex hAlign="center">
                <ShowMoreButton
                    titleHide={pl.common.hide}
                    titleShowMore={pl.common.showMore(
                        (props.intermediary.locationList.length ?? 0) - constants.tableMaxDefaultSize
                    )}
                    onClickExpand={() => {
                        setIsExpanded(true);
                    }}
                    onClickHide={() => {
                        setIsExpanded(false);
                    }}
                />
            </Flex>
        );
    }, [isExpanded, props.intermediary.locationList]);

    const locations = React.useMemo(() => {
        if (props.intermediary.locationList.length === 0) {
            return constants.noValue;
        }
        let locationsToRender;
        if (props.intermediary.locationList.length > constants.tableMaxDefaultSize && !isExpanded) {
            locationsToRender = props.intermediary.locationList.filter(
                (_, index) => index < constants.tableMaxDefaultSize
            );
        } else {
            locationsToRender = props.intermediary.locationList;
        }

        return (
            <>
                <Flex column fill gap="gap.small">
                    {locationsToRender.map((l, index) => (
                        <LocationCard
                            key={`${l.id}_${index}`}
                            address={formatAddress(l.address) ?? ""}
                            tags={getLabels(l.tagList)}
                        />
                    ))}
                </Flex>
                {expandButton}
            </>
        );
    }, [props.intermediary.locationList, isExpanded]);

    return (
        <>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.intermediaryNumber}
                content={getValuesOrDefaultRowView([props.intermediary.number])}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.status}
                content={getValuesOrDefaultRowView([props.intermediary.status?.description])}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.pd}
                content={getValuesOrDefaultRowView([props.intermediary.branch?.name])}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.dpd}
                content={getUserData(props.intermediary.director)}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.rms}
                content={getUserData(props.intermediary.salesManager)}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={`${pl.view360Card.generalInformations.startEhm} ${constants.inlineSeparator} ${pl.view360Card.generalInformations.startEhz}`}
                content={getValuesOrDefaultRowView([
                    formatDate(props.intermediary.startEHM),
                    formatDate(props.intermediary.startEHZ),
                ])}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={`${pl.view360Card.generalInformations.nip} ${constants.inlineSeparator} ${pl.view360Card.generalInformations.regon}`}
                content={getValuesOrDefaultRowView([props.intermediary.nip, props.intermediary.regon])}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.representatives}
                content={getRepresentatives()}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.agencyRegistrationData}
                content={getAgencyRegistrationData()}
            ></EntityDetailsItem>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.locations}
                content={locations}
            ></EntityDetailsItem>
            <DynamiAttributesSection attributes={props.intermediary.dynamicAttributeList} />
            <EntityDetailsItem
                label={pl.view360Card.profilesTable.title}
                wrap={props.intermediary.profileList.length > 0}
                content={getProfilesTable()}
            ></EntityDetailsItem>
            {props.intermediary.acl?.contracts &&
                <EntityDetailsItem
                    label={pl.view360Card.contractsTable.title}
                    wrap={props.intermediary.contractList.length > 0}
                    content={getContractsTable()}
                ></EntityDetailsItem>
            }
            {props.intermediary.acl?.commissionTable &&
                <EntityDetailsItem
                    label={pl.view360Card.commissionTable.title}
                    wrap={props.intermediary.commissionSystemList.length > 0}
                    content={getCommissionTablesSection()}
                ></EntityDetailsItem>
            }
        </>
    );
});
